import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { PAGE_LINK_QUERY_PARAM, usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useSequence from 'Hooks/useSequence';
import {
  addQueryWithReplace,
  getUrlFromParamSafely,
  QueryVal,
  QueryValUrl,
  removeQuery,
  removeQueryWithReplace,
} from 'Helpers/Query';
import useMessagesStatsContext from 'Context/MessagesStatsContext';
import Sentry from 'Integrations/Sentry';
import useUserContext from 'Context/UserContext';
import moment from 'moment';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { useDockedContext } from 'Context/DockedContext';
import useDebounceWithCallback from 'Hooks/useDebounceWithCallback';
import { hasRelationship } from 'Helpers/Affordances';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { UTM_SOURCE, UTM_SOURCE_EMAIL } from 'Consts/Queries';
import useAbortAllButLatest from 'Hooks/useAbortAllButLatest';
import { buildResourceFromQueryMeta } from 'Components/ComponentLibrary/Molecules/CollectionQueryForm';
import {
  SELECTABLE_FILTER_FIELD_NAMES,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ThreadsSection/components/ThreadListHeader/components/ThreadListFilters';

const MessagesContext = React.createContext(undefined);

const defaultThreadCollectionLimit = 15;

export const MESSAGES_PAGE_COMPOSE_DOCKED_ITEM_NAME = 'MessagesPageCompose';

// Actions
//  Initializing
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

// Threads
const SET_THREADS = 'set-threads';
const THREADS_LOADING_STARTED = 'threads-loading-started';
const THREADS_LOADING_SUCCEEDED = 'threads-loading-succeeded';
const THREADS_LOADING_FAILED = 'threads-loading-failed';
const SELECT_THREAD_START = 'select-thread-start';
const SELECT_THREAD_FAILED = 'select-thread-failed';
const SELECT_THREAD_SUCCEEDED = 'select-thread-succeeded';

const THREADS_PAGE_LOADING_STARTED = 'threads-page-loading-started';
const THREADS_PAGE_LOADING_SUCCEEDED = 'threads-page-loading-succeeded';
const THREADS_PAGE_LOADING_FAILED = 'threads-page-loading-failed';

const SET_SELECTED_THREAD = 'set-selected-thread';
const CLEAR_SELECTED_THREAD = 'clear-selected-thread';

const SET_IS_SEARCHING = 'set-is-searching';
const CLEAR_IS_SEARCHING = 'clear-is-searching';

// Messages
const SET_MESSAGES = 'set-messages';
const MESSAGES_INITIALIZING_STARTED = 'messages-initializing-started';
const MESSAGES_INITIALIZING_SUCCEEDED = 'messages-initializing-succeeded';
const MESSAGES_INITIALIZING_FAILED = 'messages-initializing-failed';

const MESSAGES_REFRESHING_STARTED = 'messages-refreshing-started';
const MESSAGES_REFRESHING_SUCCEEDED = 'messages-refreshing-succeeded';
const MESSAGES_REFRESHING_FAILED = 'messages-refreshing-failed';

// Thread Draft
const SET_THREAD_DRAFT_MESSAGE_RESOURCE = 'set-thread-draft-message-resource';
const THREAD_DRAFT_MESSAGE_INITIALIZING_STARTED = 'thread-draft-message-initializing-started';
const THREAD_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED = 'thread-draft-message-initializing-succeeded';
const THREAD_DRAFT_MESSAGE_INITIALIZING_FAILED = 'thread-draft-message-initializing-failed';

// Compose Draft
const SET_COMPOSE_DRAFT_MESSAGE_RESOURCE = 'set-compose-draft-message-resource';
const COMPOSE_DRAFT_MESSAGE_INITIALIZING_STARTED = 'compose-draft-message-initializing-started';
const COMPOSE_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED = 'compose-draft-message-initializing-succeeded';
const COMPOSE_DRAFT_MESSAGE_INITIALIZING_FAILED = 'compose-draft-message-initializing-failed';
const RESET_COMPOSE_DRAFT_MESSAGE_STATE = 'reset-compose-draft-message-state';
const SET_COMPOSE_DRAFT_MESSAGE_SENDING = 'set-compose-draft-message-sending';

// Sending State
const SEND_MESSAGE_STARTED = 'send-message-started';
const SEND_MESSAGE_SUCCEEDED = 'send-message-succeeded';
const SEND_MESSAGE_FAILED = 'send-message-failed';

const SET_OUT_OF_OFFICE_NOTIFICATION_RESOURCE = 'set-out-of-office-notification';

const clearMessageData = {
  isMessagesInitializing: true,
  messagesInitializingError: null,
  messagesCollection: null,
  messagesList: [],
  threadDraftMessageResource: null,
};

const init = () => {
  return {
    // General
    isInitializing: true,
    initializingError: null,
    // Threads
    isThreadsLoading: false,
    isThreadsPageLoading: false,
    threadsCollection: null,
    threadsCollectionPage: 1,
    threadLoadingError: null,
    selectedThreadResource: null,
    selectedThreadRoutingDetailsResource: null,
    selectThreadError: null,
    isSearching: false,
    // Messages
    isMessagesInitializing: true,
    messagesInitializingError: null,
    isMessagesRefreshing: false,
    messagesRefreshingError: null,
    messagesCollection: null,
    messagesList: [],
    // Selected Thread Draft
    threadDraftMessageInitializing: true,
    threadDraftMessageInitializingError: null,
    threadDraftMessageResource: null,
    // Compose Draft
    composeDraftMessageInitializing: true,
    composeDraftMessageInitializingError: null,
    composeDraftMessageResource: null,
    composeDraftMessageSending: false,
    // Message State
    messageSending: false,
    messageSendingError: null,
    outOfOfficeNotificationResource: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializingError,
      };
    }
    case SET_THREADS: {
      return {
        ...state,
        threadsCollection: action.threadsCollection,
        threads: action.threadsCollection?.resources().list(),
      };
    }
    case THREADS_LOADING_STARTED: {
      return {
        ...state,
        isThreadsLoading: true,
        threadsLoadingError: null,
      };
    }
    case THREADS_LOADING_SUCCEEDED: {
      return {
        ...state,
        threadsCollection: action.threadsCollection,
        threads: action.threadsCollection?.resources().list(),
        isThreadsLoading: false,
        threadsLoadingError: null,
      };
    }
    case THREADS_LOADING_FAILED: {
      return {
        ...state,
        isThreadsLoading: false,
        threadsLoadingError: action.threadsLoadingError,
      };
    }
    case SELECT_THREAD_START: {
      return {
        ...state,
        ...clearMessageData,
        selectThreadError: null,
      };
    }
    case SELECT_THREAD_SUCCEEDED: {
      return {
        ...state,
        selectThreadError: null,
      };
    }
    case SELECT_THREAD_FAILED: {
      return {
        ...state,
        selectThreadError: action.selectThreadError,
      };
    }
    case THREADS_PAGE_LOADING_STARTED: {
      return {
        ...state,
        isThreadsPageLoading: true,
        threadsLoadingError: null,
      };
    }
    case THREADS_PAGE_LOADING_SUCCEEDED: {
      return {
        ...state,
        isThreadsPageLoading: false,
        threadsLoadingError: null,
      };
    }
    case THREADS_PAGE_LOADING_FAILED: {
      return {
        ...state,
        isThreadsPageLoading: false,
        threadsLoadingError: action.threadsLoadingError,
      };
    }
    case SET_SELECTED_THREAD: {
      return {
        ...state,
        selectedThreadResource: action.selectedThreadResource,
        selectedThreadRoutingDetailsResource: action.selectedThreadRoutingDetailsResource,
      };
    }
    case CLEAR_SELECTED_THREAD: {
      return {
        ...state,
        ...clearMessageData,
        selectedThreadResource: null,
        selectedThreadRoutingDetailsResource: null,
      };
    }
    case SET_IS_SEARCHING: {
      return {
        ...state,
        isSearching: true,
      };
    }
    case CLEAR_IS_SEARCHING: {
      return {
        ...state,
        isSearching: false,
      };
    }
    case SET_MESSAGES: {
      return {
        ...state,
        messagesCollection: action.messagesCollection,
        messagesList: action.messagesCollection?.resources()?.list() || [],
      };
    }
    case MESSAGES_INITIALIZING_STARTED: {
      return {
        ...state,
        ...clearMessageData,
      };
    }
    case MESSAGES_INITIALIZING_SUCCEEDED: {
      return {
        ...state,
        isMessagesInitializing: false,
        messagesInitializingError: null,
        messagesCollection: action.messagesCollection,
        messagesList: action.messagesCollection?.resources()?.list() || [],
        threadDraftMessageResource: action.threadDraftMessageResource,
      };
    }
    case MESSAGES_INITIALIZING_FAILED: {
      return {
        ...state,
        isMessagesInitializing: false,
        messagesInitializingError: action.messagesInitializingError,
      };
    }
    case MESSAGES_REFRESHING_STARTED: {
      return {
        ...state,
        isMessagesRefreshing: true,
        messagesRefreshingError: null,
      };
    }
    case MESSAGES_REFRESHING_SUCCEEDED: {
      return {
        ...state,
        isMessagesRefreshing: false,
        messagesRefreshingError: null,
      };
    }
    case MESSAGES_REFRESHING_FAILED: {
      return {
        ...state,
        isMessagesRefreshing: false,
        messagesRefreshingError: action.messagesRefreshingError,
      };
    }
    case SET_THREAD_DRAFT_MESSAGE_RESOURCE: {
      return {
        ...state,
        threadDraftMessageResource: action.threadDraftMessageResource,
      };
    }
    case THREAD_DRAFT_MESSAGE_INITIALIZING_STARTED: {
      return {
        ...state,
        threadDraftMessageInitializing: true,
        threadDraftMessageInitializingError: null,
      };
    }
    case THREAD_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED: {
      return {
        ...state,
        threadDraftMessageInitializing: false,
        threadDraftMessageInitializingError: null,
      };
    }
    case THREAD_DRAFT_MESSAGE_INITIALIZING_FAILED: {
      return {
        ...state,
        threadDraftMessageInitializing: false,
        threadDraftMessageInitializingError: action.threadDraftMessageInitializingError,
      };
    }
    case SET_COMPOSE_DRAFT_MESSAGE_RESOURCE: {
      return {
        ...state,
        composeDraftMessageResource: action.composeDraftMessageResource,
      };
    }
    case COMPOSE_DRAFT_MESSAGE_INITIALIZING_STARTED: {
      return {
        ...state,
        composeDraftMessageInitializing: true,
        composeDraftMessageInitializingError: null,
      };
    }
    case COMPOSE_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED: {
      return {
        ...state,
        composeDraftMessageInitializing: false,
        composeDraftMessageInitializingError: null,
      };
    }
    case COMPOSE_DRAFT_MESSAGE_INITIALIZING_FAILED: {
      return {
        ...state,
        composeDraftMessageInitializing: false,
        composeDraftMessageInitializingError: action.composeDraftMessageInitializingError,
      };
    }
    case SET_COMPOSE_DRAFT_MESSAGE_SENDING: {
      return {
        ...state,
        composeDraftMessageSending: action.composeDraftMessageSending,
      };
    }
    case RESET_COMPOSE_DRAFT_MESSAGE_STATE: {
      return {
        ...state,
        composeDraftMessageInitializing: true,
        composeDraftMessageInitializingError: null,
        composeDraftMessageResource: null,
      };
    }
    case SEND_MESSAGE_STARTED: {
      return {
        ...state,
        messagesList: [...state.messagesList, action.inFlightMessage].filter((messageResource) => !!messageResource),
        messageSending: true,
        messageSendingError: null,
      };
    }
    case SEND_MESSAGE_SUCCEEDED: {
      return {
        ...state,
        messageSending: false,
        messageSendingError: null,
      };
    }
    case SEND_MESSAGE_FAILED: {
      return {
        ...state,
        messagesList: state.messagesCollection?.resources()?.list() || [],
        messageSending: false,
        messageSendingError: action.messageSendingError,
      };
    }
    case SET_OUT_OF_OFFICE_NOTIFICATION_RESOURCE: {
      return {
        ...state,
        outOfOfficeNotificationResource: action.outOfOfficeNotificationResource,
      };
    }
    default: {
      throw new Error(`Unhandled MessagesContext action type: ${action.type}`);
    }
  }
};

/** Provided a link, this function will get the limit and offset values used for pagination */
const getLimitsOffsetAndPageFromLink = (link) => {
  const limit = Number(QueryValUrl(new URL(link), 'limit')) || defaultThreadCollectionLimit;
  const offset = Number(QueryValUrl(new URL(link), 'offset')) || 0;
  const page = (offset / limit) + 1;

  return {
    limit,
    offset,
    page,
  };
};

const MessagesProvider = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const { pageContentResource: messagesPageResource } = usePageContentContext();
  const { handleMessagesStatsRefresh } = useMessagesStatsContext();
  const { displayErrorMessage, displayWarning } = useSnackbarContext();
  const { isAgent } = useUserContext();
  const { setHouseholdId } = useSimpleHousehold();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const {
    undockItem, isItemDocked, getItem: getDockedItem, dockItem,
  } = useDockedContext();
  const isDocked = isItemDocked(MESSAGES_PAGE_COMPOSE_DOCKED_ITEM_NAME);
  const threadSelfLink = QueryVal('threadSelfLink');
  const threadSelfLinkURL = getUrlFromParamSafely('threadSelfLink');
  const pageSelfLink = getUrlFromParamSafely(PAGE_LINK_QUERY_PARAM);
  const selectedThreadIdFromPageSelfLink = pageSelfLink?.searchParams.get('selectedThreadId');
  const householdIdFromPageSelfLink = pageSelfLink?.searchParams.get('householdId');
  const householdId = QueryVal('householdId') ?? (isAgent && householdIdFromPageSelfLink);
  const [selectedThreadId, setSelectedThreadId] = React.useState(selectedThreadIdFromPageSelfLink);
  const threadSelfLinkHouseholdId = threadSelfLinkURL && QueryValUrl(threadSelfLinkURL, 'householdId');
  const threadsHasNextPage = !!state.threadsCollection?.link('next');
  const threadsHasPrevPage = !!state.threadsCollection?.link('prev');
  let page = 1;
  if (threadsHasNextPage) {
    const linkData = getLimitsOffsetAndPageFromLink(state.threadsCollection.link('next').url());
    page = linkData.page - 1;
  } else if (threadsHasPrevPage) {
    const linkData = getLimitsOffsetAndPageFromLink(state.threadsCollection.link('prev').url());
    page = linkData.page + 1;
  }

  /** *************************************************************************************************************** */
  // region General Functions
  /** *************************************************************************************************************** */

  /** Checks that the item exists, if it does not throw an error message. */
  const throwIfDoesntExist = (item, msg) => {
    if (!item) {
      throw new Error(msg);
    }
  };

  /** Logs event on mount if source is email and current user is a member */
  const logEventOnMount = () => {
    const shouldLogNavigateFromEmailEvent = QueryVal(UTM_SOURCE) === UTM_SOURCE_EMAIL
      && !isAgent;
    if (shouldLogNavigateFromEmailEvent) {
      logStandardizedEvent(categories.clientMessages,
        eventTypes.navigateTo,
        'Messages -  Navigated to Messages from email', {
          eventProperties: {
            threadId: threadSelfLinkURL ? QueryValUrl(threadSelfLinkURL, 'id') : undefined,
          },
        });
    }
  };

  /** Sets the context's isSearching state to true */
  const setSearching = () => {
    dispatch({ type: SET_IS_SEARCHING });
  };

  /** Sets the context's isSearching state to false */
  const clearSearching = () => {
    dispatch({ type: CLEAR_IS_SEARCHING });
  };
  // endregion

  /** *************************************************************************************************************** */
  // region Threads Collection Functions
  /** *************************************************************************************************************** */

  const {
    startSequence: startThreadSequence,
    isSequenceStale: isThreadSequenceStale,
    clearSequence: clearThreadSequence,
  } = useSequence();

  /** Helper function to find a thread based on id */
  const findThread = React.useCallback((thread) => {
    return state.threadsCollection.resources().findItem((threadResource) => threadResource.id() === thread.id());
  }, [state]);

  /** Helper function that returns true if the threadResource exists in the threadsCollection state */
  const hasThread = (thread) => {
    return !!findThread(thread);
  };

  /** Clears the context's selectedThreadResourceState and removes the threadSelfLink URL Query parameter */
  const clearSelectedThreadResource = () => {
    removeQueryWithReplace('threadSelfLink');
    dispatch({ type: CLEAR_SELECTED_THREAD });
  };


  /** Sets the context's threadsCollection state */
  const setThreadsCollection = (threadsCollection) => {
    throwIfDoesntExist(threadsCollection, "threadsCollection doesn't exist");
    dispatch({ type: SET_THREADS, threadsCollection });
  };

  const setThreadsCollectionWithClear = (threadsCollection) => {
    if (state.selectedThreadResource && !threadsCollection.resources().findItem((threadResource) => threadResource.id() === state.selectedThreadResource?.id())) {
      clearSelectedThreadResource();
    }

    setThreadsCollection(threadsCollection);
  };

  /** Fetches threadLink will fetch and sets state.threadsCollection. Only completes most recent call */
  const loadThreadsLink = async (threadsLink, params = {}, showSelectedThread = false) => {
    throwIfDoesntExist(threadsLink, 'loadThreadsLink Error: Link not present');

    const sequenceId = startThreadSequence();
    dispatch({ type: THREADS_LOADING_STARTED });
    let threadsCollection;
    try {
      const paramsForThreadLink = params;
      if (showSelectedThread && (selectedThreadId || selectedThreadIdFromPageSelfLink)) {
        paramsForThreadLink.selectedThreadId = `${selectedThreadId ?? selectedThreadIdFromPageSelfLink}`;
      }

      if (householdId) {
        paramsForThreadLink.householdId = householdId;
      }

      threadsCollection = await threadsLink.get({ params: paramsForThreadLink });
      if (isThreadSequenceStale(sequenceId)) {
        return;
      }

      dispatch({ type: THREADS_LOADING_SUCCEEDED, threadsCollection });
    } catch (e) {
      if (isThreadSequenceStale(sequenceId)) {
        return;
      }
      dispatch({ type: THREADS_LOADING_FAILED, threadsLoadingError: e });
      throw e;
    }
    clearThreadSequence();

    // eslint-disable-next-line consistent-return
    return threadsCollection;
  };

  /** Refreshes the current threadsCollection. Reapplies limit/offset params b/c server removes them from self link. */
  const refreshThreads = React.useCallback(async () => {
    const params = {};
    if (threadsHasNextPage) {
      const { limit, offset } = getLimitsOffsetAndPageFromLink(state.threadsCollection.link('next').url());
      params.limit = limit;
      params.offset = offset - limit;
    } else if (threadsHasPrevPage) {
      const { limit, offset } = getLimitsOffsetAndPageFromLink(state.threadsCollection.link('prev').url());
      params.limit = limit;
      params.offset = offset + limit;
    }
    return loadThreadsLink(state.threadsCollection.link('self'), params);
  }, [state]);

  const updateThreadsForSendMessage = React.useCallback(() => {
    // Make a new collection
    const newCollection = state.threadsCollection.copy();

    // Find the selected thread from the collection (the updated thread)
    const thread = newCollection
      .resources()
      .findItem(threadItem => threadItem.id() === state.selectedThreadResource.id());

    // Mark as unread
    thread.attr('isUnread', false);
    thread.attr('previewMessageDate', moment().format());

    // Move the thread to the top of the threadCollection
    newCollection.resources().removeItem(thread.id());
    newCollection.resources().unshiftItem(thread.id(), thread);

    // Set the thread
    setThreadsCollection(newCollection);
  }, [state]);

  /** Helper function that loads the specified page (prev/next) of thread resources */
  const loadThreadsCollectionPage = React.useCallback(async (linkName) => {
    const link = state.threadsCollection.link(linkName);
    if (!link) return;

    const { limit, offset } = getLimitsOffsetAndPageFromLink(link.url());

    dispatch({ type: THREADS_PAGE_LOADING_STARTED });
    try {
      await loadThreadsLink(state.threadsCollection.link(linkName), { limit, offset }, true);
      dispatch({ type: THREADS_PAGE_LOADING_SUCCEEDED });
    } catch (e) {
      dispatch({ type: THREADS_PAGE_LOADING_FAILED, threadsLoadingError: e });
    }
  }, [state]);

  /** Helper functions to load the next and previous page of thread resources if they exist */
  const loadThreadsCollectionNextPage = async () => loadThreadsCollectionPage('next');
  const loadThreadsCollectionPreviousPage = async () => loadThreadsCollectionPage('prev');
  // endregion

  /** *************************************************************************************************************** */
  // region General Draft Message Functions
  /** *************************************************************************************************************** */

  /** Sets the context's outOfOfficeNotificationResource state
   */
  const setOutOfOfficeNotificationResource = (outOfOfficeNotificationResource) => {
    dispatch({ type: SET_OUT_OF_OFFICE_NOTIFICATION_RESOURCE, outOfOfficeNotificationResource });
  };

  /**
   * Fetches draftMessagesLink and returns the draft message collection or creates a new draft message collection with
   * a single resource */
  const loadDraftMessagesLink = async (draftMessagesLink) => {
    throwIfDoesntExist(draftMessagesLink, 'loadDraftMessagesLink Error: Link not present');

    try {
      let draftMessagesCollection = await draftMessagesLink.get();
      // Create a draft message if none are found.
      if (!draftMessagesCollection?.resources()?.head()) {
        const [newDraftMessage] = await draftMessagesCollection.create({ addToCollection: true });
        newDraftMessage.attr('isDraft', 'true');
        draftMessagesCollection = await newDraftMessage.submit();
      }
      return draftMessagesCollection;
    } catch (e) {
      throw e;
    }
  };

  /** *************************************************************************************************************** */
  // region Thread Draft Message Functions
  /** *************************************************************************************************************** */

  /** Set the context's threadDraftMessageResource state value */
  const setThreadDraftMessageResource = (newThreadDraftMessageResource) => {
    dispatch({ type: SET_THREAD_DRAFT_MESSAGE_RESOURCE, threadDraftMessageResource: newThreadDraftMessageResource });
  };

  /** Loads the current draft message for the selected thread */
  const loadCurrentDraft = React.useCallback(async () => {
    // Loads draft message from return of refreshSelectedThreadResource instead of calling refreshDraftMessages because
    // refreshDraftMessages relies on a state that is updated in refreshSelectedThreadResource.
    if (state.selectedThreadResource?.relationship(fr.relationships.draftMessages.name)?.link('related')) {
      dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_STARTED });
      try {
        const [newDraftMessageResource] = await loadDraftMessagesLink(state.selectedThreadResource?.relationship(fr.relationships.draftMessages.name)?.link('related'));
        setThreadDraftMessageResource(newDraftMessageResource);
        dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED });
      } catch (e) {
        displayErrorMessage(e, 'Error: Failed to load draft message');
        dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_FAILED, threadDraftMessageInitializingError: e });
      }
    }
  }, [state]);

  /** Refreshes the threadDraftMessage resource from its self link */
  const refreshThreadDraftMessage = React.useCallback(async () => {
    dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_STARTED });
    try {
      const [newThreadDraftMessageResource] = await loadDraftMessagesLink(state.threadDraftMessageResource.link('self'));
      setThreadDraftMessageResource(newThreadDraftMessageResource);
      dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED });
    } catch (e) {
      displayErrorMessage(e, 'Error: Failed to load draft message');
      dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_FAILED, threadDraftMessageInitializingError: e });
    }
  }, [state]);

  /** Auto saves a draft message if the content of the draft message changes */
  const autoSaveThreadDraft = useDebounceWithCallback(async (newDraftMessageResource) => {
    // If this is not a draft, exit
    if (!newDraftMessageResource.isTruthy('isDraft')) {
      return;
    }
    try {
      await newDraftMessageResource.submit();
    } catch (e) {
      Sentry.createSentryErrorLog(e, e?.status);
    }
  }, 500, []);
  // endregion

  /** *************************************************************************************************************** */
  // region Message Functions
  /** *************************************************************************************************************** */

  const {
    startSequence: startMessageInitSequence,
    isSequenceStale: isMessageInitSequenceStale,
    clearSequence: clearMessageInitSequence,
  } = useSequence();

  /** Sets the context's messagesCollection state value */
  const setMessagesCollection = (messagesCollection) => {
    throwIfDoesntExist(messagesCollection, "messagesCollection doesn't exist");
    dispatch({ type: SET_MESSAGES, messagesCollection });
  };

  /** Fetches and saves messages, threads, draft message to state. Only completes most recent call */
  const initializeThreadMessages = React.useCallback(async (thread) => {
    let messagesCollection = null;
    let threadDraftMessageResource = null;

    const sequenceId = startMessageInitSequence();
    dispatch({ type: MESSAGES_INITIALIZING_STARTED });

    try {
      const messagesLink = thread?.relationship(fr.relationships.messages.name)?.link('related');
      const draftMessagesLink = thread?.relationship(fr.relationships.draftMessages.name)?.link('related');
      throwIfDoesntExist(messagesLink, 'initializeThreadMessages Error: Messages Link not present');

      // Only fetch draft messages and messages because we only want to set the state if the action isn't stale
      // If we call loadThreadMessagesLink it will set state, and we don't want that
      messagesCollection = await messagesLink.get();

      // If thread is readOnly it won't have a draftMessagesLink
      if (draftMessagesLink) {
        dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_STARTED });
        try {
          [threadDraftMessageResource] = await loadDraftMessagesLink(draftMessagesLink);
          dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED });
        } catch (e) {
          displayErrorMessage(e, 'Error: Failed to load draft message');
          dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_FAILED, threadDraftMessageInitializingError: e });
        }
      }
      if (isMessageInitSequenceStale(sequenceId)) return;
      dispatch({ type: MESSAGES_INITIALIZING_SUCCEEDED, messagesCollection, threadDraftMessageResource });
    } catch (e) {
      if (isMessageInitSequenceStale(sequenceId)) return;
      displayErrorMessage(e, 'Error: Failed to load messages');
      dispatch({ type: MESSAGES_INITIALIZING_FAILED, messagesInitializingError: e });
    }
    clearMessageInitSequence();
  }, [state]);

  /** Fetches a messages collection from a messagesLink and sets the context's messagesCollection state value */
  const loadMessagesLink = React.useCallback(async (messagesLink) => {
    throwIfDoesntExist(messagesLink, 'loadMessagesLink Error: Link not present');

    dispatch({ type: MESSAGES_REFRESHING_STARTED });

    try {
      const messagesCollection = await messagesLink.get();

      setMessagesCollection(messagesCollection);
      dispatch({ type: MESSAGES_REFRESHING_SUCCEEDED });
    } catch (e) {
      dispatch({ type: MESSAGES_REFRESHING_FAILED, messagesRefreshingError: e });
    }
  }, [state]);

  /** Refreshes the messagesCollection from its self link. TODO: Add pagination to Messages */
  const refreshMessages = React.useCallback(async () => {
    return loadMessagesLink(state.messagesCollection.link('self'));
  }, [state.messagesCollection]);

  /** Submits message and refreshes everything afterward. This function exits if sending of an empty message */
  const sendThreadReply = React.useCallback(async (text, args = {}) => {
    const {
      avoidNotifySupport = 'true',
    } = args;

    if (!state.threadDraftMessageResource) {
      return;
    }

    if (text === '') {
      displayWarning('Cannot send an empty message. Please enter a message and try again.');
      return;
    }

    // Build the message to send, this message is added to the message list optimistically while it sends. This is why
    // editableUntil is set on the client, it is not used by the server and a correct version is returned by the server
    // in the message resource submit response
    const inFlightMessage = state.threadDraftMessageResource
      .copy()
      .attr('content', text)
      .attr('isDraft', 'false')
      .attr('canEdit', 'true')
      .attr('editableUntil', moment().add(5, 'minute'))
      .attr('publishedDate', moment());

    dispatch({ type: SEND_MESSAGE_STARTED, inFlightMessage });

    try {
      await state.threadDraftMessageResource
        .attr('content', text)
        .attr('isDraft', 'false')
        .attr('avoidNotifySupport', avoidNotifySupport)
        .submit();

      updateThreadsForSendMessage();
      await loadCurrentDraft();

      dispatch({ type: SEND_MESSAGE_SUCCEEDED });
    } catch (e) {
      if (e.errors) {
        displayErrorMessage(e, e.errors);
      } else {
        displayErrorMessage(e, 'Failed to send message, please try again.');
      }
      dispatch({ type: SEND_MESSAGE_FAILED, messageSendingError: e });
    }
  }, [state]);
  // endregion

  /** *************************************************************************************************************** */
  // region Selected Thread Resource Functions
  /** *************************************************************************************************************** */

  /** Sets state.selectedThreadResource state, simpleHouseholdContext, and threadSelfLink Query parameter */
  const setSelectedThreadResource = async (selectedThreadResource) => {
    throwIfDoesntExist(selectedThreadResource, "selectedThreadResource doesn't exist");
    setHouseholdId(selectedThreadResource.attr('householdId'));
    addQueryWithReplace({ threadSelfLink: selectedThreadResource.link('self').url() });
    setSelectedThreadId(selectedThreadResource.id());

    const selectedThreadRoutingDetailsResource = await selectedThreadResource.withRelationship(fr.relationships.threadRoutingDetails.name,
      async (rel) => {
        const [threadRoutingDetailsResource] = await rel.link('related').get();
        return threadRoutingDetailsResource;
      },
      () => null);

    dispatch({ type: SET_SELECTED_THREAD, selectedThreadResource, selectedThreadRoutingDetailsResource });
  };

  /** Refreshes the selected thread resource from its self link */
  const refreshSelectedThreadResource = React.useCallback(async () => {
    if (!state.selectedThreadResource) {
      return null;
    }

    try {
      const [thread] = await state.selectedThreadResource.link('self').get();
      setSelectedThreadResource(thread);
      return thread;
    } catch (e) {
      Sentry.createSentryErrorLog(e, e?.status);
    }
    return null;
  }, [state]);

  /** Updates the selected thread to read and submits then refreshes messageStats and threadsCollection */
  const markSelectedThreadAsRead = (thread) => {
    if (thread.isTruthy('isUnread')) {
      thread.attr('isUnread', 'false');
      // submit threads
      thread.submit()
        .then(() => {
          // We don't need to wait for this async refresh.
          // We also don't need to display this error because it is of no use to the member, just log it.
          handleMessagesStatsRefresh().catch((e) => {
            Sentry.createSentryErrorLog(e, e?.status);
          });
        })
        .catch(async e => {
          thread.attr('isUnread', 'true');
          Sentry.createSentryErrorLog(e, e?.status);
          if (e.errors) {
            displayErrorMessage(e, e.errors);
          } else {
            displayErrorMessage(e, 'Error: Failed to update thread');
          }
          await refreshThreads();
        });
    }
  };

  const clearAllFilters = async (threadResource) => {
    const metaQueries = state.threadsCollection.meta().queries().head();
    const resultCollection = await state.threadsCollection.query(metaQueries.attr('rel'), (link) => {
      const linkCopy = link.copy().withQueryParams();
      const queryResource = buildResourceFromQueryMeta(state.threadsCollection, metaQueries.export());
      Object.keys(queryResource.attributes()).forEach(attr => {
        if (SELECTABLE_FILTER_FIELD_NAMES.includes(attr)) {
          linkCopy.queryParamFilter(attr, null, 'eq');
        } else {
          linkCopy.queryParamFilter(attr, queryResource.attr(attr), 'eq');
        }
      });
      return linkCopy.get();
    });

    if (!resultCollection.resources().findItem((resource) => resource.id() === threadResource.id())) {
      resultCollection.resources().unshiftItem(threadResource.id(), threadResource);
    }

    setThreadsCollection(resultCollection);
  };

  /** Selects a thread, if user is a member marks the thread as read, and initializes thread messages */
  const selectThread = async (threadResource) => {
    if (!threadResource) {
      const e = new Error('Attempting to select undefined thread resource');
      displayErrorMessage(e, 'Error: Failed to open thread. If this issue persists please contact support.');
      clearSelectedThreadResource();
      return;
    }
    dispatch({ type: SELECT_THREAD_START });
    // mark selected thread as read if on member website
    if (!isAgent) {
      markSelectedThreadAsRead(threadResource);
    }
    // set selected thread
    setSelectedThreadResource(threadResource);

    // clear all filters if selected thread is not in thread collection
    if (state.threadsCollection && !state.threadsCollection.resources().findItem((resource) => resource.id() === threadResource.id())) {
      clearAllFilters(threadResource);
    }

    // initialize messages and draftMessages
    try {
      await initializeThreadMessages(threadResource);
      dispatch({ type: SELECT_THREAD_SUCCEEDED });
    } catch (e) {
      dispatch({ type: SELECT_THREAD_FAILED, selectThreadError: e });
    }
  };

  /** Selects a thread from a threadLink. If the thread doesn't exist, clear the threadSelfLink value */
  const selectThreadFromLink = async (threadLink) => {
    try {
      const [selectedThread] = await fr.link({ data: { url: threadLink } }).get();
      // If the fetched thread exists
      if (selectedThread) {
        await selectThread(selectedThread);
      }

      // If not, remove the query param
      if (!selectedThread || !hasThread(selectedThread)) {
        removeQueryWithReplace('threadSelfLink');
      }
    } catch (e) {
      // If we failed to load the thread from the link, or if the thread doesn't exist in the list
      // clear the param from the url and log
      removeQueryWithReplace('threadSelfLink');
    }
  };
  // endregion

  /** *************************************************************************************************************** */
  // region Compose Draft Message Functions
  /** *************************************************************************************************************** */

  const {
    newSignal: composeDraftNewSignal,
    complete: composeDraftComplete,
  } = useAbortAllButLatest();

  /** Set the context's composeDraftMessageResource state value */
  const setComposeDraftMessageResource = (newComposeDraftMessageResource) => {
    dispatch({ type: SET_COMPOSE_DRAFT_MESSAGE_RESOURCE, composeDraftMessageResource: newComposeDraftMessageResource });
  };

  const setComposeDraftMessageSubject = React.useCallback((text) => {
    state.composeDraftMessageResource.attr('subject', text);
    setComposeDraftMessageResource(state.composeDraftMessageResource);
  }, [state]);

  const setComposeDraftInternalMessageRecipients = React.useCallback((recipients) => {
    state.composeDraftMessageResource.attr('messageRecipients', recipients);
    setComposeDraftMessageResource(state.composeDraftMessageResource);
  }, [state]);

  const setComposeDraftExternalMessageRecipients = React.useCallback((recipients) => {
    state.composeDraftMessageResource.attr('externalMessageRecipients', recipients);
    setComposeDraftMessageResource(state.composeDraftMessageResource);
  }, [state]);

  /** Fetches existing draft or creates a new draft for composing a new thread and message */
  const loadOrCreateDraftForCompose = async (draftHouseholdId) => {
    if (!draftHouseholdId) {
      Sentry.createSentryErrorLog(new Error('Attempted to load draft for compose without a household ID'));
      return null;
    }

    let composeNewMessageCollection;

    dispatch({ type: COMPOSE_DRAFT_MESSAGE_INITIALIZING_STARTED });
    try {
      composeNewMessageCollection = await messagesPageResource
        .relationship(fr.relationships.composeNewMessage.name)
        ?.link('related')
        .get({ params: { householdId: draftHouseholdId } });
    } catch (e) {
      displayErrorMessage(e, 'Error: Failed to open compose dock. Please reach out to support if error persists.');
      return null;
    }

    // Create a new draft message if no existing draft message is found
    try {
      if (!composeNewMessageCollection?.resources()?.head()) {
        const [newDraftMessage] = composeNewMessageCollection.create({ addToCollection: true });
        newDraftMessage.attr('isDraft', 'true');
        composeNewMessageCollection = await newDraftMessage.submit();
      }
    } catch (e) {
      displayErrorMessage(e, 'Error: Failed to open compose dock. Please reach out to support if error persists.');
      return null;
    }

    const newComposeDraftMessageResource = composeNewMessageCollection.resources().head();

    setComposeDraftMessageResource(newComposeDraftMessageResource);

    dispatch({ type: COMPOSE_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED });

    return newComposeDraftMessageResource;
  };

  /** Clears the state for composeDraftMessage */
  const clearComposeDraftMessageResource = () => {
    dispatch({ type: RESET_COMPOSE_DRAFT_MESSAGE_STATE });
  };

  /** Submits the compose draft message for publish */
  const sendComposeDraft = React.useCallback(async () => {
    dispatch({ type: SET_COMPOSE_DRAFT_MESSAGE_SENDING, composeDraftMessageSending: true });

    if (state.composeDraftMessageResource.attr('content') === '') {
      displayWarning('Cannot compose an empty message. Please enter a message and try again.');
      return;
    }

    state.composeDraftMessageResource.attr('isDraft', 'false');
    state.composeDraftMessageResource.attr('canEdit', 'true');
    state.composeDraftMessageResource.attr('publishedDate', moment());

    try {
      const [submittedMessageResource] = await state.composeDraftMessageResource.submit();
      undockItem('MessagesPageCompose', {
        skipSave: true,
      });
      clearComposeDraftMessageResource();
      await refreshThreads();
      if (hasRelationship(submittedMessageResource, fr.relationships.thread.name)) {
        selectThreadFromLink(submittedMessageResource.relationship(fr.relationships.thread.name).link('related').url());
      }
    } catch (e) {
      if (e.errors) {
        displayErrorMessage(e, e.errors);
      } else {
        displayErrorMessage(e, 'Error: Failed to send new message.');
      }
    }
    dispatch({ type: SET_COMPOSE_DRAFT_MESSAGE_SENDING, composeDraftMessageSending: false });
  }, [state]);

  /** Refreshes the composeDraftMessage resource from its self link */
  const refreshComposeDraftMessage = React.useCallback(async () => {
    try {
      const [newComposeDraftMessageResource] = await loadDraftMessagesLink(state.composeDraftMessageResource.link('self'));
      setComposeDraftMessageResource(newComposeDraftMessageResource);
    } catch (e) {
      displayErrorMessage(e, 'Error: Failed to load draft message');
    }
  }, [state]);

  /** Auto saves a draft message if the content of the draft message changes */
  const actuallAutoSaveComposeDraft = useDebounceWithCallback(async (newDraftMessageResource) => {
    const { signal } = composeDraftNewSignal();

    // If this is not a draft, exit
    if (!newDraftMessageResource.isTruthy('isDraft')) {
      return;
    }
    try {
      const [responseMessageResource] = await newDraftMessageResource.submit({ signal });
      if (!signal.aborted) {
        setComposeDraftMessageResource(responseMessageResource);
      }
    } catch (e) {
      if (!signal.aborted) {
        // on error reset the value
        Sentry.createSentryErrorLog(e, e?.status);
      }
    }

    if (!signal.aborted) {
      composeDraftComplete();
    }
  }, 500, []);
  // We can't just use the actual auto save function because that one is debounced, so if someone types quickly enough to avoid the debounced function running, the signal won't be aborted and the response from the server will overwrite what they're typing
  const autoSaveComposeDraft = useCallback((newDraftMessageResource) => {
    composeDraftNewSignal(); // This always runs, regardless of debouncing, causing any draft saving that's in-progress to be aborted
    actuallAutoSaveComposeDraft(newDraftMessageResource);
  }, []);
  // endregion

  /** *************************************************************************************************************** */
  // region Other Functions
  /** *************************************************************************************************************** */

  /** Set householdId param on URL and threadSelfLink, and load threadsCollection from link */
  const initialize = async () => {
    dispatch({ type: INITIALIZE_STARTED });

    // Get threadsLink
    const threadsLink = messagesPageResource
      .relationship(fr.relationships.threads.name)
      .link('related');

    // Get threadSelfLink householdID Param
    let threadLinkHouseholdID;
    if (threadSelfLinkURL) {
      threadLinkHouseholdID = QueryValUrl(threadSelfLinkURL, 'householdId');
    }

    // Use the threadLink's householdId param if it exists and is valid.
    // Otherwise, use the householdId param if it exists and is valid.
    if (!threadLinkHouseholdID && householdId && householdId > 0) {
      threadsLink.equal('householdId', householdId);
      addQueryWithReplace({ householdId });
    }

    if (threadLinkHouseholdID && threadLinkHouseholdID > 0) {
      threadsLink.equal('householdId', threadLinkHouseholdID);
      addQueryWithReplace({ householdId: threadLinkHouseholdID });
    }

    let threadCollection;
    try {
      threadCollection = await loadThreadsLink(threadsLink, {}, true);
      dispatch({ type: INITIALIZE_SUCCEEDED });
    } catch (e) {
      dispatch({ type: INITIALIZE_FAILED, initializingError: e });
    }

    const selectedThreadResource = threadCollection.resources().findItem((threadResource) => {
      return threadResource.id() === `${selectedThreadId ?? selectedThreadIdFromPageSelfLink}`;
    });

    if (selectedThreadResource && !threadSelfLinkURL) {
      selectThread(selectedThreadResource);
    }

    // If we have a threadSelfLink load it. Checking threadSelfLinkURL because this only exists if
    // threadSelfLink is a valid link and can be turned into a "new URL(threadSelfLink)"
    if (threadSelfLinkURL) {
      // Do not await this because it is not required to complete to finish init.

      selectThreadFromLink(threadSelfLink)
        .catch(e => displayErrorMessage(e, 'Error: Failed to load thread'));
    }
  };

  /** Refreshes the threads, selectedThreadResource, threadDraftMessage if it exists and messages */
  const refreshAll = React.useCallback(async () => {
    try {
      await refreshThreads();
      let thread;

      // if we refreshed the threads and they no longer have the selected thread resource in them, do not refresh the selected thread resource
      if (hasThread(state.selectedThreadResource)) {
        thread = await refreshSelectedThreadResource();
      } else {
        clearSelectedThreadResource();
      }

      // Must load draft message based on return from refreshSelectedThreadResource instead of called refreshThreadDraftMessage because
      // refreshThreadDraftMessage relies on a state that is updated in refreshSelectedThreadResource.
      if (thread?.relationship(fr.relationships.draftMessages.name)?.link('related')) {
        dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_STARTED });
        try {
          const [newThreadDraftMessageResource] = await loadDraftMessagesLink(thread?.relationship(fr.relationships.draftMessages.name)?.link('related'));
          setThreadDraftMessageResource(newThreadDraftMessageResource);
          dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED });
        } catch (e) {
          displayErrorMessage(e, 'Error: Failed to load draft message');
          dispatch({ type: THREAD_DRAFT_MESSAGE_INITIALIZING_FAILED, threadDraftMessageInitializingError: e });
        }
      } else {
        setThreadDraftMessageResource(null);
      }

      await refreshMessages();
    } catch (err) {
      displayErrorMessage(err, 'Error: Failed to refresh messages and threads');
    }
  }, [state]);
  // endregion

  const selectThreadWithSelectedThreadID = async () => {
    const threadsLink = messagesPageResource
      .relationship(fr.relationships.threads.name)
      .link('related');
    const threadCollection = await loadThreadsLink(threadsLink, {}, true);

    const selectedThreadResource = threadCollection.resources().findItem((threadResource) => {
      return threadResource.id() === `${selectedThreadId ?? selectedThreadIdFromPageSelfLink}`;
    });

    if (selectedThreadResource) {
      selectThread(selectedThreadResource);
    }
  };

  /** *************************************************************************************************************** */
  // region Effects
  /** *************************************************************************************************************** */

  /** On householdId query param change, load a new threads collection with that query param */
  React.useEffect(() => {
    const cb = async () => {
      // If still initializing, return
      if (state.isInitializing) {
        return;
      }

      // If there is no household id, undock the compose modal.
      if (!householdId) {
        undockItem('MessagesPageCompose');
      }

      // We always want to clear selected thread resource on householdId query param change
      clearSelectedThreadResource();

      // Set the draft message resource used to compose a message to null
      dispatch({ type: RESET_COMPOSE_DRAFT_MESSAGE_STATE });

      // The code below is not ideal, but in the case of a redirect link, because of how a redirect link works,
      // we may have selectedThreadId and householdId on the page self link. While these are necessary to load the correct thread,
      // we need to delete them after load (householdId needs to be added back as its own param) so that they don't cause issues when changing households.
      if (pageSelfLink && isAgent) {
        const pageSelfLinkUrl = new URL(pageSelfLink);
        const pageSelfLinkParams = pageSelfLinkUrl.searchParams;

        // delete params from page self link
        pageSelfLinkParams.delete('selectedThreadId');
        pageSelfLinkParams.delete('householdId');
        pageSelfLinkUrl.search = pageSelfLinkParams.toString();

        // replace page self link and household id query params
        if (householdId) {
          addQueryWithReplace({ householdId }, { pageSelfLink: pageSelfLinkUrl.toString() });
        }

        // replace only page self link if not household id query param
        if (!householdId) {
          addQueryWithReplace({ pageSelfLink: pageSelfLinkUrl.toString() });
        }
      }

      // If selected thread id in page self link, select thread
      if (selectedThreadIdFromPageSelfLink && isAgent) {
        selectThreadWithSelectedThreadID();
        return;
      }

      const openComposeFromPageSelfLink = pageSelfLink?.searchParams.get('openCompose');
      if (openComposeFromPageSelfLink === 'true') {
        const draft = await loadOrCreateDraftForCompose(householdIdFromPageSelfLink);
        dockItem(
          MESSAGES_PAGE_COMPOSE_DOCKED_ITEM_NAME,
          'New Message',
          draft.getParentCollection(),
          {
            autoSave: false,
            saved: false,
            displaySavedState: false,
          },
        );
      }

      if (state.threadsCollection) {
      // Build new threads link by removing the householdId param from the link
      // and replacing it with the new household id. This allows for the threads
      // with the new household id to be fetched.
        const threadsLink = state.threadsCollection.link('self').copy();
        threadsLink.removeSearchParam('householdId');
        if (householdId) {
          threadsLink.equal('householdId', householdId);
        }

        // If the current user is a member and household id is undefined, loading the threads link will fail.
        // Navigating to a page which uses page context via the left nav bar will change the url before the
        // page context resource is loaded. Therefore, this useEffect may be called for a member with an
        // undefined household id.
        if (!householdId && !isAgent) {
          return;
        }

        // Load the threads link with the new household id.
        const { limit, offset } = getLimitsOffsetAndPageFromLink(threadsLink.url());
        loadThreadsLink(threadsLink, { limit, offset })
          .catch(e => {
            displayErrorMessage(e, 'Error: Failed to load threads');
          });
      }
    };
    cb();
  }, [householdId, state.isInitializing]);

  // If we refresh with the item already docked, we need to finish initializing by setting compose draft message resource to the docked item
  React.useEffect(() => {
    if (!isDocked || !state.composeDraftMessageInitializing) {
      return;
    }

    const messagePageComposeDockedItem = getDockedItem(MESSAGES_PAGE_COMPOSE_DOCKED_ITEM_NAME);
    if (!messagePageComposeDockedItem.collection) {
      return;
    }

    setComposeDraftMessageResource(messagePageComposeDockedItem.collection.resources().head());
    dispatch({ type: COMPOSE_DRAFT_MESSAGE_INITIALIZING_SUCCEEDED });
  }, [isDocked]);


  /** This effect is to log if the selectedThread and the threadDraft are ever out of sync!
   * This is for logging purposes only */
  React.useEffect(() => {
    // If either one don't exist, no need to check and alert.
    if (!state?.threadDraftMessageResource?.attr('threadId')) {
      return;
    }
    if (!state?.selectedThreadResource?.id()) {
      return;
    }

    // If the threadDraftResource's thread ID doesn't match the selected thread
    // Alert sentry
    if (state.threadDraftMessageResource.attr('threadId') !== state.selectedThreadResource.id().toString()) {
      const error = new Error(
        `Messages Page: ThreadDraftMessageResource ID: ${state.threadDraftMessageResource.attr('threadId')}
        doesn't match the SelectedThreadResource ID: ${state.selectedThreadResource.id()}`,
      );
      Sentry.createSentryErrorLog(error);
    }
  }, [state.threadDraftMessageResource, state.selectedThreadResource]);


  /** An effect listener to watch if the selected thread resource doesn't exist but messages is finished initializing */
  React.useEffect(() => {
    // If selected thread resource exists, no need to log
    if (state?.selectedThreadResource) {
      return;
    }
    // If messages initializing is true, no need to log
    if (state.isMessagesInitializing) {
      return;
    }
    // If the isMessagesInitializing is false (messages have loaded) but the selected thread resource is null
    // Alert sentry
    if (!state.isMessagesInitializing && !state.selectedThreadResource) {
      const error = new Error(
        `Messages have loaded with selectedThreadResource as null or undefined
         selectedThreadResource: ${state.selectedThreadResource},
         isMessagesInitializing: ${state.isMessagesInitializing},
         selectedThreadID: ${state.messagesCollection?.resources()?.head()?.attr('threadId')},
         messagesInitializingError: ${state.messagesInitializingError},
         selectThreadError: ${state.selectThreadError}
        `,
      );
      Sentry.createSentryErrorLog(error);
      clearSelectedThreadResource();
    }
  }, [state.selectedThreadResource, state.isMessagesInitializing]);


  /** On mount run initialize function */
  useComponentDidMount(async () => {
    logEventOnMount();
    await initialize();
  }, () => {
    removeQuery(UTM_SOURCE);
  });
  // endregion

  const memoizedValue = React.useMemo(() => {
    return {
      // General
      householdId,
      threadSelfLink,
      threadSelfLinkHouseholdId,
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      isSearching: state.isSearching,
      // Threads
      isThreadsLoading: state.isThreadsLoading,
      threadsLoadingError: state.threadsLoadingError,
      threadsCollection: state.threadsCollection,
      threadsCollectionPage: page,
      threadsHasNextPage,
      threadsHasPrevPage,
      threads: state.threads,
      isThreadsPageLoading: state.isThreadsPageLoading,
      // Thread Draft
      threadDraftMessageInitializing: state.threadDraftMessageInitializing,
      threadDraftMessageInitializingError: state.threadDraftMessageInitializingError,
      threadDraftMessageResource: state.threadDraftMessageResource,
      // Compose Draft
      composeDraftMessageInitializing: state.composeDraftMessageInitializing,
      composeDraftMessageInitializingError: state.composeDraftMessageInitializingError,
      composeDraftMessageResource: state.composeDraftMessageResource,
      composeDraftMessageSending: state.composeDraftMessageSending,
      // Messages
      messagesCollection: state.messagesCollection,
      messagesList: state.messagesList,
      isMessagesInitializing: state.isMessagesInitializing,
      messagesInitializingError: state.messagesInitializingError,
      isMessagesRefreshing: state.isMessagesRefreshing,
      messagesRefreshingError: state.messagesRefreshingError,
      messageSending: state.messageSending,
      // Selected Thread
      selectedThreadResource: state.selectedThreadResource,
      selectedThreadRoutingDetailsResource: state.selectedThreadRoutingDetailsResource,
      selectThreadError: state.selectThreadError,
      // Other
      outOfOfficeNotificationResource: state.outOfOfficeNotificationResource,
      // Functions
      // General
      setSearching,
      clearSearching,
      //  Threads
      loadThreadsLink,
      refreshThreads,
      setThreadsCollection,
      setThreadsCollectionWithClear,
      loadThreadsCollectionNextPage,
      loadThreadsCollectionPreviousPage,
      // General Draft
      loadDraftMessagesLink,
      // Thread Draft
      refreshThreadDraftMessage,
      setThreadDraftMessageResource,
      autoSaveThreadDraft,
      // Compose Draft
      clearComposeDraftMessageResource,
      setComposeDraftMessageResource,
      setComposeDraftMessageSubject,
      setComposeDraftInternalMessageRecipients,
      setComposeDraftExternalMessageRecipients,
      loadOrCreateDraftForCompose,
      sendComposeDraft,
      refreshComposeDraftMessage,
      autoSaveComposeDraft,
      // Messages
      loadMessagesLink,
      refreshMessages,
      sendThreadReply,
      // Selected Thread
      setSelectedThreadResource,
      clearSelectedThreadResource,
      selectThread,
      markSelectedThreadAsRead,
      refreshSelectedThreadResource,
      // Other
      setOutOfOfficeNotificationResource,
      refreshAll,
    };
  }, [
    state,
    // General
    setSearching,
    clearSearching,
    //  Threads
    loadThreadsLink,
    refreshThreads,
    setThreadsCollection,
    loadThreadsCollectionNextPage,
    loadThreadsCollectionPreviousPage,
    // General Draft
    loadDraftMessagesLink,
    // Thread Draft
    refreshThreadDraftMessage,
    setThreadDraftMessageResource,
    autoSaveThreadDraft,
    // Compose Draft
    clearComposeDraftMessageResource,
    setComposeDraftMessageResource,
    setComposeDraftMessageSubject,
    setComposeDraftInternalMessageRecipients,
    setComposeDraftExternalMessageRecipients,
    loadOrCreateDraftForCompose,
    sendComposeDraft,
    refreshComposeDraftMessage,
    autoSaveComposeDraft,
    // Messages
    loadMessagesLink,
    refreshMessages,
    sendThreadReply,
    // Selected Thread
    setSelectedThreadResource,
    clearSelectedThreadResource,
    selectThread,
    markSelectedThreadAsRead,
    refreshSelectedThreadResource,
    // Other
    setOutOfOfficeNotificationResource,
    refreshAll,
  ]);

  return (
    <MessagesContext.Provider value={memoizedValue}>
      {children}
    </MessagesContext.Provider>
  );
};

// Use Messages State Context
const useMessagesContext = () => {
  const context = React.useContext(MessagesContext);
  if (context === undefined) {
    throw new Error('useMessagesContext must be used within a MessagesProvider');
  }
  return context;
};

MessagesProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  MessagesProvider,
  useMessagesContext,
};
